import {
  useState, useEffect 
} from 'react';
import {FormModal} from '@/components/ui/form-modal';
import {
  USER_ROLE_LABELS,
  INTERNAL_ROLES,
  SUPPLIER_ROLES,
  isSupplierRole,
  type User,
  type UpdateUserRequest,
} from '@/features/settings';
import {
  UserHeader,
  ProfileTab,
  AccessTab,
  ActionsTab,
  Timestamps,
} from './edit-user-tabs';

const INTERNAL_ROLE_OPTIONS = INTERNAL_ROLES.map(role => ({
  value: role,
  label: USER_ROLE_LABELS[role],
}));

const SUPPLIER_ROLE_OPTIONS = SUPPLIER_ROLES.map(role => ({
  value: role,
  label: USER_ROLE_LABELS[role],
}));

type TabId = 'profile' | 'access' | 'actions';

interface SelectOption {
  value: string;
  label: string;
}

interface EditUserModalProps {
  readonly user: User | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: (userId: string, data: UpdateUserRequest) => Promise<void>;
  readonly onAction: (userId: string, action: 'disable' | 'enable' | 'reset-password' | 'resend-invitation' | 'delete') => Promise<void>;
  readonly supplierOptions: SelectOption[];
  readonly siteOptions: SelectOption[];
  readonly supplierName?: string;
}

export function EditUserModal({
  user,
  isOpen,
  onClose,
  onSave,
  onAction,
  supplierOptions,
  siteOptions,
  supplierName,
}: EditUserModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('profile');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<UpdateUserRequest>({});

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber ?? '',
        jobTitle: user.jobTitle ?? '',
        department: user.department ?? '',
        role: user.role,
        siteAccess: user.siteAccess,
        venueAccess: user.venueAccess,
        defaultSiteId: user.defaultSiteId,
        supplierId: user.supplierId,
        preferredLanguage: user.preferredLanguage ?? 'en',
      });
      setActiveTab('profile');
    }
  }, [user]);

  if (!user) return null;

  const isSupplier = isSupplierRole(user.role);
  const roleOptions = isSupplier ? SUPPLIER_ROLE_OPTIONS : INTERNAL_ROLE_OPTIONS;

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      await onSave(user.userId, formData);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAction = async (action: 'disable' | 'enable' | 'reset-password' | 'resend-invitation' | 'delete') => {
    setIsSubmitting(true);
    try {
      await onAction(user.userId, action);
      if (action === 'delete') {
        onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs: Array<{
    id: TabId;
    label: string 
  }> = [
    {
      id: 'profile',
      label: 'Profile' 
    },
    {
      id: 'access',
      label: 'Access' 
    },
    {
      id: 'actions',
      label: 'Actions' 
    },
  ];

  return (
    <FormModal
      title="Edit User"
      description={`${user.firstName} ${user.lastName}`}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      saveLabel={isSubmitting ? 'Saving...' : 'Save Changes'}
      saveDisabled={isSubmitting || activeTab === 'actions'}
      hideSaveButton={activeTab === 'actions'}
    >
      <div className="space-y-6">
        <UserHeader user={user} />
        
        <div className="border-b">
          <nav className="flex gap-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`border-b-2 px-1 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'profile' && (
          <ProfileTab formData={formData} setFormData={setFormData} user={user} />
        )}
        {activeTab === 'access' && (
          <AccessTab
            formData={formData}
            setFormData={setFormData}
            roleOptions={roleOptions}
            siteOptions={siteOptions}
            supplierOptions={supplierOptions}
            isSupplier={isSupplier}
            supplierName={supplierName}
          />
        )}
        {activeTab === 'actions' && (
          <ActionsTab user={user} onAction={handleAction} isSubmitting={isSubmitting} />
        )}

        <Timestamps user={user} />
      </div>
    </FormModal>
  );
}
